import { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { IconImage } from '@hivebrite/design-system/Icon';
import { Loader } from '@hivebrite/design-system/Loader';
import { useMeasure } from '@hivebrite/react-hooks';

import Drop from 'hb-builder/components/common/Drop';
import ImageSetter from 'hb-builder/components/common/ImageSetter';
import EmptyState from 'hb-builder/components/common/state/EmptyState';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { Wrapper, WrapperBuilder } from 'hb-builder/components/common/Wrapper';
import { ALLOWED_IMAGE_EXTENSIONS } from 'hb-builder/constants/allowedImagesExtensions';
import { defaultTabs } from 'hb-builder/constants/tabs';
import {
  useBuilderContext,
  useComponentContext,
  useBuilderConfigContext,
  useModalContext,
  useRendererContext,
  useSectionContext,
} from 'hb-builder/hooks';
import { getJustifyContent } from 'hb-builder/utils/attributes';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { PageImageItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/pageImageItemAttributess';
import { overrideFileName } from 'hb-react/backoffice/utils/overrideFileName';
import mergeRefs from 'hb-react/shared/utils/mergeRefs';

import ImageSettings from './ImageSettings';

const componentType = 'single';
const emptyStateHeight = '200px';

const EmailImage = () => <div />; // never used

const RenderBuilder = () => {
  const fileInput = useRef<HTMLInputElement | null>(null);
  const { updateAttribute, updateAttributes } = useBuilderContext();
  const { item } = useComponentContext();

  const {
    features: {
      image: { onSave },
    },
  } = useBuilderConfigContext();
  const [ref, { width: wrapperWidth }] = useMeasure();
  const { setModalOpen } = useModalContext();
  const { nbFinalChildren, sectionHeight } = useSectionContext();

  const attributes = item?.attributes;

  const handleInputFileChange = (e) => {
    const [file] = e.target.files;

    const finalFile = overrideFileName({ file });

    updateAttribute({
      id: item.id,
      attribute: '__loading',
      value: finalFile,
    });
  };

  const handleSaveImage = useCallback(async (img) => {
    if (ALLOWED_IMAGE_EXTENSIONS.includes(img.type)) {
      const image = await onSave(img);

      if (!image) {
        return;
      }

      const imgNode = new window.Image();

      imgNode.src = image;
      imgNode.onload = () => {
        updateAttributes({
          id: item.id,
          values: [{ attribute: 'imageSrc', value: image }],
        });
      };
    }
  }, []);

  useEffect(() => {
    if (attributes?.__loading) {
      handleSaveImage(attributes.__loading);
    }
  }, [attributes?.__loading, handleSaveImage]);

  const { imageSrc } = item?.attributes || {};
  const imageWidth = attributes?.imageFullWidth ? '100%' : 'min-content';
  const isEmpty = !imageSrc;
  const isNarrow = wrapperWidth < 200;

  const handleEmptyDoubleClick = () =>
    setModalOpen((prevIsModalOpen) => ({
      ...prevIsModalOpen,
      [item.id]: true,
    }));

  const renderEmptyComponent = ({
    isDragging,
    isOver,
  }: {
    isDragging: boolean;
    isOver: boolean;
  }) =>
    isDragging ? null : (
      <EmptyState
        dataTestId="empty-state"
        height={emptyStateHeight}
        isOver={isOver}
        nbFinalChildren={nbFinalChildren}
        onDoubleClick={handleEmptyDoubleClick}
        p={isNarrow ? 0 : 4}
        sectionHeight={sectionHeight}
      >
        <Box
          alignItems="center"
          color="primary"
          flexDirection="column"
          textAlign="center"
        >
          <IconImage size={50} />
          {!isNarrow && (
            <Box color="black">
              {s__(
                'Builder|Double click on the component to add an image or add it in the floating panel',
              )}
            </Box>
          )}
        </Box>
        <StyledInput
          name="styled-input"
          key={item.id}
          onChange={handleInputFileChange}
          accept={ALLOWED_IMAGE_EXTENSIONS.toString()}
          ref={fileInput}
          type="file"
        />
      </EmptyState>
    );

  const renderComponent = ({ isDragging }: { isDragging: boolean }) =>
    isDragging ? null : (
      <ImgStyled
        alt={attributes?.imageAlt}
        src={imageSrc}
        style={{
          borderRadius: attributes?.imageRadius,
          width: imageWidth,
        }}
      />
    );

  return (
    <>
      <Drop accept={['single']}>
        {({ isOver, ref: dropRef }) =>
          attributes?.isLoadingSrc ? (
            <ImgLoaderWrapper>
              <Loader size={40} />
            </ImgLoaderWrapper>
          ) : (
            // @ts-expect-error untyped
            <WrapperBuilder
              attributes={attributes}
              dataTestId="builder-component-image"
              isEmpty={isEmpty}
              ref={mergeRefs([ref, dropRef])}
              style={{
                // Safari's default setting for the align-items is stretch
                alignItems: 'flex-start',
                justifyContent: getJustifyContent(attributes?.horizontalAlign),
                ...(isEmpty && { height: '100%' }),
              }}
            >
              {({ isDragging }) =>
                isEmpty
                  ? renderEmptyComponent({ isDragging, isOver })
                  : renderComponent({ isDragging })
              }
            </WrapperBuilder>
          )
        }
      </Drop>
      <ImageSetter attributeName="imageSrc" item={item} />
    </>
  );
};

// needed for the template preview thumbnails
const Render = ({
  item,
}: RenderProps<DocumentItem<PageImageItemAttributes>>) => {
  const { isMobilePreview } = useRendererContext();
  const { attributes } = item || {};

  return (
    // @ts-expect-error untyped
    <Wrapper
      attributes={attributes}
      componentType={componentType}
      dataTestId="builder-component-image"
      isMobilePreview={isMobilePreview}
      style={{
        // Safari's default setting for the align-items is stretch
        alignItems: 'flex-start',
        justifyContent: getJustifyContent(attributes?.horizontalAlign),
      }}
    >
      <ImgStyled
        alt={attributes?.imageAlt}
        src={attributes?.imageSrc}
        style={{
          borderRadius: attributes?.imageRadius,
          width: attributes?.imageWidth,
        }}
      />
    </Wrapper>
  );
};

EmailImage.componentName = 'EmailImage';
EmailImage.componentType = componentType;

EmailImage.componentLabel = __('Image');
EmailImage.componentCategory = 'single';
EmailImage.componentIcon = () => IconImage;

EmailImage.canBeDoubleClicked = true;
EmailImage.canBeDragged = true;
EmailImage.canBeListed = true;
EmailImage.canBeSelected = true;

EmailImage.item = () => ({
  attributes: {
    horizontalAlign: 'center',
  },
  category: EmailImage.componentCategory,
  name: EmailImage.componentName,
  type: EmailImage.componentName,
});

EmailImage.toolbar = ['edit', 'drag', 'duplicate', 'delete'];

EmailImage.Settings = ImageSettings;
EmailImage.SettingsTabs = defaultTabs;

EmailImage.RenderBuilder = RenderBuilder;
EmailImage.Render = Render;

export default EmailImage;

const ImgLoaderWrapper = styled(Box).attrs(() => ({
  alignItems: 'center',
  color: 'fog',
  height: emptyStateHeight,
  justifyContent: 'center',
  p: 2,
  width: '100%',
}))``;

const ImgStyled = styled.img`
  display: block;
  pointer-events: none;
`;

const StyledInput = styled.input`
  visibility: hidden;
  position: absolute;
  top: 0;
`;
